import { useMutation, useQueryClient } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { QueryKeys } from '../../../../../enums/queryKeys'
import { moveDocument, moveFolder } from '../../../query-api'
import { MoveItem } from '../types/moveItemType'

interface UseMoveDocumentOrFolderProps {
  onSuccess?: () => void
  onError?: () => void
}

interface MoveItemsParams {
  items: MoveItem[]
  sourceFolderId: string
  parentFolderId: string | null
}

export const useMoveDocumentOrFolder = ({ onSuccess, onError }: UseMoveDocumentOrFolderProps) => {
  const { organization } = useUserOrganization()
  const queryClient = useQueryClient()

  const { mutate, isLoading } = useMutation(
    async ({ items, parentFolderId }: MoveItemsParams) => {
      if (!organization) {
        return
      }

      await Promise.all(
        items.map((item) => {
          if (item.type === 'folder') {
            return moveFolder({ organizationId: organization.id, folderId: item.id, parentFolderId })
          }
          if (item.type === 'document' && parentFolderId) {
            return moveDocument({ organizationId: organization.id, documentId: item.id, folderId: parentFolderId })
          }
          return Promise.resolve()
        }),
      )
    },
    {
      onSuccess: (_, { items, sourceFolderId, parentFolderId }) => {
        const hasFolderItems = items.some((item) => item.type === 'folder')

        if (hasFolderItems) {
          queryClient.invalidateQueries(QueryKeys.DmsFolderContents)
          queryClient.invalidateQueries(QueryKeys.DmsFolders)
        } else {
          queryClient.invalidateQueries([QueryKeys.DmsFolderContents, sourceFolderId])
          queryClient.invalidateQueries([QueryKeys.DmsFolderContents, parentFolderId])
        }
        onSuccess?.()
      },
      onError: () => {
        onError?.()
      },
    },
  )

  return {
    moveItems: mutate,
    isLoading,
  }
}
